import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { Client } from '../types/client';
import { crmService } from '../services/crmService';
import { useDebounce } from '../hooks/useDebounce';
import AddClientModal from '../components/AddClientModal';
import ModifyClientModal from '../components/ModifyClientModal';

const ClientsPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms delay

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const fetchClients = async (search: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await crmService.getClients(search);
      setClients(data);
    } catch (err) {
      setError('Failed to fetch clients.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const handleModifyClick = (client: Client) => {
    setSelectedClient(client);
    setIsModifyModalOpen(true);
  };

  const handleSave = () => {
    fetchClients(debouncedSearchTerm);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Clients</Typography>
        <Button variant="contained" onClick={() => setIsAddModalOpen(true)}>Add Client</Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label="Search Clients (by Name, Email, or Phone)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Address</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.map((client) => (
              <TableRow key={client.id}>
                <TableCell>{client.name}</TableCell>
                <TableCell>{client.email || 'N/A'}</TableCell>
                <TableCell>{client.phone || 'N/A'}</TableCell>
                <TableCell>{client.address || 'N/A'}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleModifyClick(client)}><EditIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <AddClientModal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSave={handleSave} />
      <ModifyClientModal open={isModifyModalOpen} onClose={() => setIsModifyModalOpen(false)} onSave={handleSave} client={selectedClient} />
    </Box>
  );
};

export default ClientsPage;